import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';
import { Spinner } from '../../components/ui/Spinner';
import ProductCard from '../../components/products/ProductCard';

const HomePage = () => {
  const [selectedProductTab, setSelectedProductTab] = useState(null);
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [courses, setCourses] = useState({});
  const [currentCourseSlide, setCurrentCourseSlide] = useState(0);

  const getCourseDescription = (course) =>
    course?.curriculum?.[0]?.description || 'Professional training program';

  const getCourseDuration = (course) => {
    if (course?.duration?.isOneDay) {
      return 'One Day Workshop';
    }

    if (course?.duration?.months !== undefined && course?.duration?.months !== null) {
      return `${course.duration.months} Months`;
    }

    if (typeof course?.duration === 'string' && course.duration.trim()) {
      return course.duration;
    }

    return 'Duration available on request';
  };

  const slides = [
    {
      image: '/images/slider/slider-0.jpg',
      alt: 'Brissbella Services 1',
    },
    {
      image: '/images/slider/slider-1.jpg',
      alt: 'Brissbella Services 2',
    },
    {
      image: '/images/slider/slider-2.jpg',
      alt: 'Brissbella Services 3',
    },
  ];

  // Auto-rotate slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [slides.length]); // Added slides.length as dependency

  // Fetch data only once on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productsRef = ref(db, 'products');
        const snapshot = await get(productsRef);
        const data = snapshot.val();

        if (data) {
          const { categories: categoriesData, products: productsData } = data;

          if (categoriesData && categoriesData.length > 0) {
            setCategories(categoriesData);
            // Set initial tab only if not already set
            setSelectedProductTab((prev) => prev || categoriesData[0].id);
          }

          if (productsData) {
            setProducts(productsData);
          }
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); // Empty dependency array - run only once on mount

  // Optional: Debug state changes without causing re-renders
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('State Update:', {
        products,
        categories,
        selectedProductTab,
        currentCategoryProducts: products[selectedProductTab],
      });
    }
  }, [products, categories, selectedProductTab]);

  const handleCategoryChange = (categoryId) => {
    setSelectedProductTab(categoryId);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRef = ref(db, 'courses');
        const snapshot = await get(coursesRef);
        if (snapshot.exists()) {
          setCourses(snapshot.val());
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setCourses({}); // Set empty courses instead of error
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Add this useEffect for course auto-rotation
  useEffect(() => {
    if (Object.keys(courses).length > 0) {
      const timer = setInterval(() => {
        setCurrentCourseSlide(
          (prev) => (prev + 1) % Object.keys(courses).length,
        );
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(timer);
    }
  }, [courses]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with Slider */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10">
              {/* Title with gradient underline effect */}
              <div className="relative">
                <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                  <span className="text-[4rem] bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                    Brissbella Beauty & Wellness
                  </span>
                </h1>
                <div className="absolute -bottom-4 left-0 w-1/3 h-1 bg-gradient-to-r from-primary-600 to-transparent"></div>
              </div>

              {/* Description with improved typography */}
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-xl">
                Welcome to Sri Lanka's premier beauty destination where
                traditional wellness meets modern expertise. We offer
                professional salon services, beauty education, and authentic
                herbal products.
              </p>

              {/* Enhanced button design */}
              <div className="pt-4">
                <Link
                  to="/about"
                  className="group relative inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-full overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-primary-700 to-primary-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="relative flex items-center">
                    Read Our Story
                    <svg
                      className="w-6 h-6 ml-3 transform group-hover:translate-x-2 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>

            {/* Enhanced slider with overlay and border effect */}
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent z-10"></div>
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    currentSlide === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}

              {/* Enhanced slider controls */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentSlide === index
                        ? 'bg-white scale-125'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Verticals Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Experience the Brissbella Difference
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Where beauty meets excellence in every aspect
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Academy Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group">
              <div className="h-64 relative overflow-hidden">
                <img
                  src="/images/academy-logo.jpg"
                  alt="Brissbella Academy"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Beauty Education Excellence
                </h3>
                <p className="text-gray-600 mb-4">
                  Professional beauty education with NVQ certification. Start
                  your journey in beauty therapy with our comprehensive courses.
                </p>
                <Link
                  to="/academy"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 group-hover:translate-x-2 transition-transform"
                >
                  <span>Visit Academy</span>
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Salon Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group">
              <div className="h-64 relative overflow-hidden">
                <img
                  src="/images/salon-logo.jpg"
                  alt="Brissbella VIP Salon"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Premium Beauty Services
                </h3>
                <p className="text-gray-600 mb-4">
                  Experience luxury beauty treatments at Kandy's premier salon.
                  Professional care for both men and women.
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 group-hover:translate-x-2 transition-transform"
                >
                  <span>Visit Salon</span>
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Products Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group">
              <div className="h-64 relative overflow-hidden">
                <img
                  src="/images/products-logo.jpg"
                  alt="Brissbella Products"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Herbal Beauty Products
                </h3>
                <p className="text-gray-600 mb-4">
                  Discover our exclusive range of natural beauty and wellness
                  products. Quality herbal solutions for your beauty needs.
                </p>
                <Link
                  to="/store"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 group-hover:translate-x-2 transition-transform"
                >
                  <span>Shop Now</span>
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Treatments Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Brissbella unisex salon
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-xl text-gray-700 leading-relaxed">
                Experience personalized beauty treatments delivered by our
                expert team. From signature hair styling to advanced skincare,
                each service is thoughtfully designed to deliver exceptional
                results while ensuring your comfort and satisfaction.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Hair Treatments Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-48">
                <img
                  src="/images/services/hair-treatments.jpg"
                  alt="Hair Treatments"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <h3 className="text-2xl font-semibold text-white">
                    Hair Treatments
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Gents Hair Cut</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 1,000
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Hair Color</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 6,500
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Keratin Treatment</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 25,000
                    </span>
                  </div>
                </div>
                <Link
                  to="/services"
                  className="mt-6 block text-center bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  View All Services
                </Link>
              </div>
            </div>

            {/* Nail Treatments Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-48">
                <img
                  src="/images/services/nail-treatments.jpg"
                  alt="Nail Treatments"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <h3 className="text-2xl font-semibold text-white">
                    Nail Treatments
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Pedicure</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 4,900
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Manicure</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 3,900
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Gel Nails</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 7,000
                    </span>
                  </div>
                </div>
                <Link
                  to="/services"
                  className="mt-6 block text-center bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  View All Services
                </Link>
              </div>
            </div>

            {/* Skin Treatments Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-48">
                <img
                  src="/images/services/skin-treatments.jpg"
                  alt="Skin Treatments"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <h3 className="text-2xl font-semibold text-white">
                    Skin Treatments
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Facial</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 5,000
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Cleanup</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 3,000
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800">Body Wax</span>
                    <span className="text-primary-600 font-semibold">
                      Rs. 2,500
                    </span>
                  </div>
                </div>
                <Link
                  to="/services"
                  className="mt-6 block text-center bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Products */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Brissbella Wellness Store
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-xl text-gray-700 leading-relaxed">
                Discover our curated collection of herbal beauty products,
                crafted with time-tested Ayurvedic ingredients. Each product
                combines Sri Lankan botanical traditions with modern skincare
                science for authentic, effective care.
              </p>
            </div>
          </div>
          <div className="flex justify-center mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8">
                {Array.isArray(categories) &&
                  categories.map((category) => (
                    <button
                      key={category.id}
                      className={`
                        pb-4 px-1 border-b-2 font-medium text-sm
                        ${
                          selectedProductTab === category.id
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                      onClick={() => handleCategoryChange(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
              </nav>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {products[selectedProductTab] ? (
              Object.values(products[selectedProductTab]).map((product) => (
                <Link
                  key={product.id}
                  to={`/store?category=${selectedProductTab}#${product.id}`}
                  className="block h-full"
                >
                  <ProductCard product={product} hideControls={true} />
                </Link>
              ))
            ) : (
              <p className="col-span-4 text-center text-gray-500">
                No products found in this category
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Special Body Pack Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center space-y-6">
                <h2 className="text-3xl font-semibold text-primary-600">
                  Brissbella Special Body Pack
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p className="text-lg">
                    Experience the healing power of nature with our exclusive
                    range of 24 specialized body packs, carefully crafted using
                    premium herbal ingredients.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Natural ingredients for deep nourishment
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Customized solutions for different skin types
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Traditional herbal remedies with modern formulations
                    </li>
                    <li className="flex items-center">
                      <span className="text-primary-600 mr-2">•</span>
                      Complete body care from head to toe
                    </li>
                  </ul>
                </div>
                <div className="flex gap-4">
                  <Link
                    to="/store"
                    className="inline-flex items-center px-8 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Shop Now
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img
                  src="/images/body-pack.jpg"
                  alt="Brissbella Body Pack Collection"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-6">
                  <p className="text-white text-lg font-medium">
                    Discover the secret to radiant, healthy skin
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academy Section - Moved near bottom */}
      <section className="py-16 pb-32">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Brissbella Academy
            </h2>
            <p className="text-gray-600 mt-4">
              Begin your professional beauty journey with our comprehensive
              training programs. Our NVQ-certified courses combine hands-on
              practice with industry insights, preparing you for a successful
              career in beauty and wellness.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <Spinner />
            </div>
          ) : error ? (
            <div className="text-center text-red-600">{error}</div>
          ) : (
            <div className="relative">
              <div className="relative">
                {Object.values(courses).map((course, index) => (
                  <div
                    key={course.id || `course-${index}`}
                    className={`transition-opacity duration-500 ${
                      currentCourseSlide === index
                        ? 'opacity-100'
                        : 'opacity-0 absolute inset-0'
                    }`}
                  >
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-[500px_minmax(0,1fr)]">
                      <div className="relative mx-auto aspect-square w-full max-w-[500px] overflow-hidden rounded-lg md:mx-0">
                        <img
                          src={course.imageUrl || '/images/academy-logo.jpg'}
                          alt={course.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="h-[500px] flex flex-col">
                        <div className="flex-grow space-y-4">
                          <div>
                            <h4 className="text-2xl font-semibold mb-3">
                              {course.name}
                            </h4>
                            <p className="text-xl text-gray-600 leading-relaxed">
                              {getCourseDescription(course)}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-6 shadow-sm space-y-4">
                            <div className="flex items-center text-gray-700">
                              <div>
                                <p className="text-sm text-gray-500">
                                  Duration
                                </p>
                                <p className="text-lg font-medium">{getCourseDuration(course)}</p>
                              </div>
                            </div>
                            <div className="flex items-center text-gray-700">
                              <div>
                                <p className="text-sm text-gray-500">
                                  Academic Level
                                </p>
                                <p className="text-lg font-medium capitalize">
                                  {course.academicLevel}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-6">
                            <p className="text-gray-700 leading-relaxed">
                              At Brissbella Academy, we provide high-quality
                              professional beauty therapy education. Our
                              curriculums are designed by industry experts to
                              help you gain the most in-demand skills. Learn in
                              our modern classrooms from well-qualified
                              lecturers who are passionate about your success.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 mt-4">
                          <Link
                            to="/academy"
                            className="flex-1 text-center bg-primary-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-primary-700 transition-colors"
                          >
                            Learn More
                          </Link>
                          <a
                            href="https://wa.me/94777437130"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-md text-lg font-medium hover:bg-primary-50 transition-colors"
                          >
                            Apply Now
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Director/About Section */}
      <section className="relative bg-primary-900">
        <div className="absolute inset-0">
          <img
            src="/images/about.png"
            alt="About Background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6 pt-4 pb-8">
              <p className="text-lg">Director, Professional Beautician</p>
              <h2 className="text-4xl md:text-5xl font-bold">
                Hello, I'm <span className="text-white">Mashi Nehansa</span>
              </h2>
              <p>
                I'm Mashi Nehansa, founder of Brissbella and a certified beauty
                professional. Our journey began with a vision to create a space
                where quality beauty services meet professional education and
                natural wellness solutions.
              </p>
              <p>
                Today, Brissbella stands as a trusted name in Sri Lanka's beauty
                industry, known for our commitment to excellence in education,
                authentic herbal products, and premium salon services. We
                continue to grow while staying true to our core values of
                quality, tradition, and innovation.
              </p>
              <p className="text-sm italic">
                (NVQ4 Qualified, High Diploma in Beauty Culture & Hairdressing,
                High Diploma in Bridal Dressing)
              </p>
            </div>
            <div className="relative h-[500px]">
              <img
                src="/images/mashi.png"
                alt="Mashi Nehansa"
                className="absolute bottom-0 right-0 max-h-[600px] w-auto"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
